// Egyptian cities and areas — comprehensive coverage of all 27 governorates.
// Each governorate is represented by its capital city; major secondary cities
// (e.g. Mansoura in Dakahlia, Tanta in Gharbia) keep their own top-level entry
// because that's what users actually search for.
//
// Keep slugs lowercase-hyphenated and stable — they're in the sitemap and
// linked from src/data/cityGuides.ts. Adding a new city here also needs a
// (minimal) cityGuideFacts entry so the /guides routes don't 404 for it.

export interface Area {
  name: string;
  name_en?: string;
  name_ar?: string;
  slug: string;
  /** English description. Sync target: areas.description_en. */
  description: string;
  /**
   * Egyptian Arabic translation of `description`. Sync target:
   * areas.description_ar. When missing, the frontend falls back to
   * the English version (see AreaDetail.tsx). Add new translations
   * here AND re-run `tsx scripts/sync-locations-to-db.ts --apply`.
   */
  description_ar?: string;
  keywords: string[];
}

export interface City {
  name: string;
  slug: string;
  description: string;
  /** Egyptian Arabic translation of the city description. */
  description_ar?: string;
  areas: Area[];
  name_ar?: string;
  name_en?: string;
}

// Tiny helper so we stop hand-writing the same 7 keyword strings per area.
const kw = (areaEn: string): string[] => {
  const a = areaEn.toLowerCase();
  return [
    `flower delivery ${a}`,
    `buy flowers ${a}`,
    `flower shop ${a}`,
    `online bouquet ${a}`,
    `best florist ${a}`,
    `send roses to ${a}`,
    `same day flower delivery ${a}`,
  ];
};

export const egyptianLocations: City[] = [
  // ========================================================================
  // CAIRO REGION (Cairo, Giza, Qalyubia)
  // ========================================================================
  {
    name: "Cairo",
    name_en: "Cairo",
    name_ar: "القاهرة",
    slug: "cairo",
    description: "The vibrant capital of Egypt, where modern life meets ancient history. Cairo offers countless opportunities for beautiful flower deliveries across its diverse neighborhoods.",
    areas: [
      { name: "Zamalek", name_en: "Zamalek", name_ar: "الزمالك", slug: "zamalek",
        description: "An upscale island district in the heart of Cairo, known for its art galleries, cafes, and beautiful Nile views. Perfect for elegant flower arrangements and romantic gestures.",
        keywords: kw("zamalek") },
      { name: "Maadi", name_en: "Maadi", name_ar: "المعادي", slug: "maadi",
        description: "A leafy suburban area popular with families and expatriates. Known for its tree-lined streets and peaceful atmosphere, ideal for celebrating special occasions with fresh flowers.",
        keywords: kw("maadi") },
      { name: "New Cairo", name_en: "New Cairo", name_ar: "القاهرة الجديدة", slug: "new-cairo",
        description: "A modern satellite city with contemporary compounds and shopping centers. Home to many young families who appreciate beautiful flower arrangements for their homes.",
        keywords: kw("new cairo") },
      { name: "Heliopolis", name_en: "Heliopolis", name_ar: "مصر الجديدة", slug: "heliopolis",
        description: "A historic suburb known for its distinctive architecture and presidential palace. A perfect location for elegant flower deliveries and special celebrations.",
        keywords: kw("heliopolis") },
      { name: "Nasr City", name_en: "Nasr City", name_ar: "مدينة نصر", slug: "nasr-city",
        description: "A large residential and commercial district home to many government buildings and educational institutions. Great for corporate flower arrangements and gifts.",
        keywords: kw("nasr city") },
      { name: "Mohandessin", name_en: "Mohandessin", name_ar: "المهندسين", slug: "mohandessin",
        description: "A bustling central district packed with offices, restaurants, and embassies. Reliable same-day delivery for both home and corporate gifting.",
        keywords: kw("mohandessin") },
      { name: "Garden City", name_en: "Garden City", name_ar: "جاردن سيتي", slug: "garden-city",
        description: "A historic, leafy district along the Nile filled with embassies and elegant villas. Perfect for refined floral arrangements.",
        keywords: kw("garden city") },
      { name: "Downtown Cairo", name_en: "Downtown Cairo", name_ar: "وسط البلد", slug: "downtown",
        description: "The cultural and commercial heart of Cairo, home to landmark buildings, cafes, and shops. Quick delivery to offices and apartments.",
        keywords: kw("downtown cairo") },
      { name: "Manial", name_en: "Manial", name_ar: "المنيل", slug: "manial",
        description: "A central residential island known for Cairo University Faculty of Medicine and its riverside corniche. Convenient for hospital and home deliveries.",
        keywords: kw("manial") },
      { name: "Fifth Settlement", name_en: "Fifth Settlement", name_ar: "التجمع الخامس", slug: "fifth-settlement",
        description: "Also known as Tagamoa El Khames — a modern New Cairo neighborhood with compounds, schools, and shopping plazas. Popular for premium gifting.",
        keywords: kw("fifth settlement") },
      { name: "Mokattam", name_en: "Mokattam", name_ar: "المقطم", slug: "mokattam",
        description: "A residential plateau overlooking Cairo with calm streets and stunning sunset views. Smooth delivery to villas and apartments.",
        keywords: kw("mokattam") },
      { name: "Helwan", name_en: "Helwan", name_ar: "حلوان", slug: "helwan",
        description: "A historic southern Cairo district with industrial, residential, and university zones. Same-day delivery available across the area.",
        keywords: kw("helwan") },
      { name: "Shubra", name_en: "Shubra", name_ar: "شبرا", slug: "shubra",
        description: "A densely populated northern district with a strong community character. Reliable doorstep delivery for family occasions.",
        keywords: kw("shubra") },
      { name: "Ain Shams", name_en: "Ain Shams", name_ar: "عين شمس", slug: "ain-shams",
        description: "A large eastern Cairo district home to Ain Shams University. Trusted delivery for student graduations and family events.",
        keywords: kw("ain shams") },
      { name: "Madinaty", name_en: "Madinaty", name_ar: "مدينتي", slug: "madinaty",
        description: "A self-contained compound city east of Cairo with parks, schools, and clubhouses. Same-day delivery direct to compound gates.",
        keywords: kw("madinaty") },
      { name: "Rehab City", name_en: "Rehab City", name_ar: "الرحاب", slug: "rehab",
        description: "A well-established gated community in New Cairo with shaded streets and a town center. Trusted neighborhood delivery.",
        keywords: kw("rehab city") },
      { name: "Korba", name_en: "Korba", name_ar: "الكوربة", slug: "korba",
        description: "A heritage Heliopolis pocket of belle-epoque arcades, Baron Empain-era facades, and cafe-lined Baghdad Street. Served from our Heliopolis hub, popular for elegant doorstep arrangements to the classic apartments above.",
        keywords: kw("korba") },
      { name: "Roxy", name_en: "Roxy", name_ar: "روكسي", slug: "roxy",
        description: "A bustling Heliopolis micro-district anchored by Roxy Square and the cinema, with side streets full of restaurants and pharmacies. Same-day drops handled out of our Heliopolis hub.",
        keywords: kw("roxy") },
      { name: "Abdeen", name_en: "Abdeen", name_ar: "عابدين", slug: "abdeen",
        description: "A historic downtown-grid district built around Abdeen Palace, with century-old apartment blocks and government ministries. Rapid moped delivery to offices and walk-ups.",
        keywords: kw("abdeen") },
      { name: "Abbassia", name_en: "Abbassia", name_ar: "العباسية", slug: "abbassia",
        description: "A dense north-central district known for the Coptic Cathedral, Ain Shams faculties of medicine and engineering, and the demerdash hospital. Reliable for hospital and graduation deliveries.",
        keywords: kw("abbassia") },
      { name: "Sayyida Zeinab", name_en: "Sayyida Zeinab", name_ar: "السيدة زينب", slug: "sayyida-zeinab",
        description: "A historic, deeply local neighborhood centered on the famous Sayyida Zeinab mosque and the surrounding old-Cairo lanes. Quick doorstep delivery for family condolences and celebrations.",
        keywords: kw("sayyida zeinab") },
      { name: "Bab El Sharia", name_en: "Bab El Sharia", name_ar: "باب الشعرية", slug: "bab-el-sharia",
        description: "An old Fatimid-Cairo quarter of narrow lanes, traditional cafes, and Al-Hussein-adjacent markets. Same-day delivery on foot from our Downtown couriers.",
        keywords: kw("bab el sharia") },
      { name: "Ataba", name_en: "Ataba", name_ar: "العتبة", slug: "ataba",
        description: "The famously busy commercial heart of central Cairo, home to the Ataba Square wholesale markets and Postal Museum. Express delivery between shops and apartments.",
        keywords: kw("ataba") },
      { name: "El Marg", name_en: "El Marg", name_ar: "المرج", slug: "el-marg",
        description: "A large family neighborhood in far northeastern Cairo at the end of the metro Line 1. Trusted same-day drops to apartment blocks and family homes.",
        keywords: kw("el marg") },
      { name: "El Salam", name_en: "El Salam", name_ar: "مدينة السلام", slug: "el-salam",
        description: "A working-family district east of Heliopolis along the Ismailia desert road, with the El Salam hospital and large residential blocks. Same-day moped delivery from our Heliopolis hub.",
        keywords: kw("el salam") },
      { name: "El Obour", name_en: "El Obour", name_ar: "العبور", slug: "el-obour",
        description: "A planned satellite city northeast of Cairo with Golf City, Obour Market, and large industrial zones. Daily delivery runs scheduled out from New Cairo.",
        keywords: kw("el obour") },
      { name: "El Shorouk", name_en: "El Shorouk", name_ar: "الشروق", slug: "el-shorouk",
        description: "A newly-developed satellite city east of Cairo on the Suez road, with the British University in Egypt and Dar Misr compounds. Same-day delivery to gated communities.",
        keywords: kw("el shorouk") },
      { name: "Tagamoa El Khames", name_en: "Tagamoa El Khames", name_ar: "التجمع الخامس", slug: "tagamoa-khames",
        description: "The booming Fifth Settlement extension covering Cairo Festival City, Point 90, and AUC New Cairo. Premium same-day drops to compound gates and office towers.",
        keywords: kw("tagamoa khames") },
      { name: "El Katameya", name_en: "El Katameya", name_ar: "القطامية", slug: "katameya",
        description: "An upscale plateau district known for Katameya Heights golf, Police Academy, and the Ring Road corridor. Reliable villa-gate delivery across compounds.",
        keywords: kw("katameya") },
      { name: "El Maasara", name_en: "El Maasara", name_ar: "المعصرة", slug: "maasara",
        description: "A southern Cairo industrial-residential district near the cement works and metro Line 1 terminus. Trusted same-day delivery to family homes.",
        keywords: kw("maasara") },
      { name: "Hadayek El Maadi", name_en: "Hadayek El Maadi", name_ar: "حدائق المعادي", slug: "hadayek-maadi",
        description: "A leafy eastern Maadi extension of mid-rise apartment blocks and family compounds, just off the Autostrad. Smooth doorstep delivery served from our Maadi hub.",
        keywords: kw("hadayek el maadi") },
      { name: "Maadi Degla", name_en: "Maadi Degla", name_ar: "ديجلا المعادي", slug: "maadi-degla",
        description: "An upmarket lettered-street Maadi pocket beloved by expat families, with leafy streets and the famous Road 9 cafe strip nearby. Same-day delivery from the Maadi hub.",
        keywords: kw("maadi degla") },
      { name: "Old Maadi", name_en: "Old Maadi", name_ar: "المعادي القديمة", slug: "old-maadi",
        description: "The original Maadi grid with the British-built villas, Maadi Sporting Club, and Road 9. Trusted villa-gate delivery for anniversaries and brunch surprises.",
        keywords: kw("old maadi") },
      { name: "El Basatin", name_en: "El Basatin", name_ar: "البساتين", slug: "el-basatin",
        description: "A densely populated neighborhood between Maadi and Dar El Salam, with strong community character and busy local markets. Quick moped delivery to apartments.",
        keywords: kw("el basatin") },
      { name: "Dar El Salam", name_en: "Dar El Salam", name_ar: "دار السلام", slug: "dar-el-salam",
        description: "A family-oriented southern Cairo district along the metro Line 1, packed with side-street markets and schools. Reliable same-day delivery for celebrations and condolences.",
        keywords: kw("dar el salam") },
    ],
  },

  {
    name: "Giza",
    name_en: "Giza",
    name_ar: "الجيزة",
    slug: "giza",
    description: "Home to the iconic Pyramids and Sphinx, Giza combines ancient wonders with modern living. A perfect destination for memorable flower deliveries and special celebrations.",
    areas: [
      { name: "6th of October", name_en: "6th of October", name_ar: "السادس من أكتوبر", slug: "6th-of-october",
        description: "A modern satellite city with numerous residential compounds and industrial areas. Popular with families who appreciate beautiful flower arrangements for their homes.",
        keywords: kw("6th of october") },
      { name: "Sheikh Zayed", name_en: "Sheikh Zayed", name_ar: "الشيخ زايد", slug: "sheikh-zayed",
        description: "An upscale residential area known for its luxury compounds and proximity to Smart Village. Perfect for elegant flower deliveries and corporate gifts.",
        keywords: kw("sheikh zayed") },
      { name: "Dokki", name_en: "Dokki", name_ar: "الدقي", slug: "dokki",
        description: "A central district known for its government buildings and residential areas. Close to the Nile, making it ideal for romantic flower deliveries.",
        keywords: kw("dokki") },
      { name: "Agouza", name_en: "Agouza", name_ar: "العجوزة", slug: "agouza",
        description: "A central neighborhood along the Nile corniche, full of clinics, residential buildings, and offices. Fast delivery for any occasion.",
        keywords: kw("agouza") },
      { name: "Haram", name_en: "Haram", name_ar: "الهرم", slug: "haram",
        description: "The Pyramids-adjacent neighborhood, a mix of residential streets and tourist destinations. Reliable same-day delivery.",
        keywords: kw("haram") },
      { name: "Faisal", name_en: "Faisal", name_ar: "فيصل", slug: "faisal",
        description: "A large family neighborhood between Dokki and the Pyramids. Trusted doorstep delivery across its many side streets.",
        keywords: kw("faisal") },
      { name: "Imbaba", name_en: "Imbaba", name_ar: "إمبابة", slug: "imbaba",
        description: "A densely populated northern Giza district. Quick delivery for family occasions and home celebrations.",
        keywords: kw("imbaba") },
      { name: "Bulaq El Dakrour", name_en: "Bulaq El Dakrour", name_ar: "بولاق الدكرور", slug: "bulaq-el-dakrour",
        description: "A bustling working-family district between Dokki and Faisal, anchored by the busy Bulaq Dakrour train station and dense apartment blocks. Trusted moped delivery for birthdays and family gatherings.",
        keywords: kw("bulaq el dakrour") },
      { name: "El Warraq", name_en: "El Warraq", name_ar: "الوراق", slug: "el-warraq",
        description: "A northern Giza district along the Nile near Warraq Island, with strong community character and busy local markets. Reliable doorstep delivery from our Mohandessin couriers.",
        keywords: kw("el warraq") },
      { name: "Awsim", name_en: "Awsim", name_ar: "أوسيم", slug: "awsim",
        description: "A semi-rural town in northern Giza beyond Imbaba, mixing farmland with growing apartment blocks. Same-day delivery scheduled from our Giza hub.",
        keywords: kw("awsim") },
      { name: "El Mariotia", name_en: "El Mariotia", name_ar: "المريوطية", slug: "el-mariotia",
        description: "A busy market-and-residential strip along the Mariotia Canal near Faisal, with workshop streets and apartment blocks. Quick same-day delivery for family events.",
        keywords: kw("el mariotia") },
      { name: "Hadayek El Ahram", name_en: "Hadayek El Ahram", name_ar: "حدائق الأهرام", slug: "hadayek-el-ahram",
        description: "A family-oriented suburb southwest of the Pyramids, full of mid-rise apartments and the Hadayek Ahram club. Trusted same-day drops out of our Haram couriers.",
        keywords: kw("hadayek el ahram") },
      { name: "Saqyat Mekky", name_en: "Saqyat Mekky", name_ar: "ساقية مكي", slug: "saqyat-mekky",
        description: "A dense residential pocket between Dokki and Bulaq Dakrour, with the famous Saqyat Mekky police station as a landmark. Reliable apartment delivery.",
        keywords: kw("saqyat mekky") },
      { name: "Mit Oqba", name_en: "Mit Oqba", name_ar: "ميت عقبة", slug: "mit-oqba",
        description: "A historic densely-built neighborhood wedged between Mohandessin and Agouza, with the Mit Oqba mosque and busy local markets. Quick moped delivery from the Mohandessin hub.",
        keywords: kw("mit oqba") },
      { name: "Atfeh", name_en: "Atfeh", name_ar: "أطفيح", slug: "atfeh",
        description: "An agricultural town in far-southern Giza along the Nile. Scheduled next-day delivery from the Giza hub.",
        keywords: kw("atfeh") },
      { name: "Mansouriya", name_en: "Mansouriya", name_ar: "المنصورية", slug: "mansouriya",
        description: "A semi-rural village near the Pyramids plateau, dotted with horse stables and small farms. Same-day delivery to villas and stables from our Haram hub.",
        keywords: kw("mansouriya") },
      { name: "Kerdasa", name_en: "Kerdasa", name_ar: "كرداسة", slug: "kerdasa",
        description: "A historic Giza town famous for its handwoven textiles, bazaars, and traditional Egyptian crafts. Reliable next-day delivery from our Giza couriers.",
        keywords: kw("kerdasa") },
      { name: "El Saff", name_en: "El Saff", name_ar: "الصف", slug: "el-saff",
        description: "A farming town in southern Giza along the Nile, with growing residential extensions on the desert edge. Scheduled delivery from the Cairo-Giza axis.",
        keywords: kw("el saff") },
    ],
  },

  {
    name: "Banha",
    name_en: "Banha",
    name_ar: "بنها",
    slug: "banha",
    description: "Capital of Qalyubia governorate, just north of Cairo. A growing Delta hub with university, agricultural, and residential districts.",
    areas: [
      { name: "Banha City Center", name_en: "Banha City Center", name_ar: "وسط مدينة بنها", slug: "banha-center",
        description: "The downtown core with markets, schools, and government offices. Reliable next-day delivery from Cairo.",
        keywords: kw("banha") },
      { name: "Qaha", name_en: "Qaha", name_ar: "قها", slug: "qaha",
        description: "A residential and industrial town in Qalyubia. Delivery available for family and corporate occasions.",
        keywords: kw("qaha") },
      { name: "Shubra El Kheima", name_en: "Shubra El Kheima", name_ar: "شبرا الخيمة", slug: "shubra-el-kheima",
        description: "A large industrial city in southern Qalyubia, just north of Cairo. Same-day delivery from the Cairo hub.",
        keywords: kw("shubra el kheima") },
      { name: "Khanka", name_en: "Khanka", name_ar: "الخانكة", slug: "khanka",
        description: "An eastern Qalyubia town on the Ismailia road with strong industrial activity and the famous Khanka mental hospital. Same-day delivery from the Cairo hub.",
        keywords: kw("khanka") },
      { name: "Qalyub", name_en: "Qalyub", name_ar: "قليوب", slug: "qalyub",
        description: "A historic Qalyubia town directly north of Shubra, with the Qalyub agricultural research center and busy local markets. Reliable same-day delivery.",
        keywords: kw("qalyub") },
      { name: "Toukh", name_en: "Toukh", name_ar: "طوخ", slug: "toukh",
        description: "A Delta-edge town in Qalyubia known for its Friday market and Nile-branch farms. Next-day delivery from Banha and the Cairo hub.",
        keywords: kw("toukh") },
    ],
  },

  // ========================================================================
  // ALEXANDRIA & WESTERN DELTA (Alexandria, Beheira, Matruh)
  // ========================================================================
  {
    name: "Alexandria",
    name_en: "Alexandria",
    name_ar: "الإسكندرية",
    slug: "alexandria",
    description: "The beautiful Mediterranean city known as the Pearl of the Mediterranean. Alexandria's coastal charm makes it perfect for romantic flower deliveries and seaside celebrations.",
    areas: [
      { name: "Smouha", name_en: "Smouha", name_ar: "سموحة", slug: "smouha",
        description: "A modern residential area popular with families and professionals. Known for its shopping centers and proximity to the beach, perfect for fresh flower deliveries.",
        keywords: kw("smouha") },
      { name: "Stanley", name_en: "Stanley", name_ar: "ستانلي", slug: "stanley",
        description: "A glamorous beachfront area with stunning Mediterranean views. Ideal for romantic occasions and elegant flower arrangements by the sea.",
        keywords: kw("stanley") },
      { name: "Sidi Gaber", name_en: "Sidi Gaber", name_ar: "سيدي جابر", slug: "sidi-gaber",
        description: "A central district connecting various parts of Alexandria. A hub for business and residential activities, perfect for both personal and corporate flower deliveries.",
        keywords: kw("sidi gaber") },
      { name: "Gleem", name_en: "Gleem", name_ar: "جليم", slug: "gleem",
        description: "A vibrant coastal neighborhood known for its beaches and lively atmosphere. Great for celebrating summer occasions with colorful flower arrangements.",
        keywords: kw("gleem") },
      { name: "Roushdy", name_en: "Roushdy", name_ar: "روشدي", slug: "roushdy",
        description: "An upscale residential and commercial district along the corniche. Reliable delivery to apartments and cafes.",
        keywords: kw("roushdy") },
      { name: "San Stefano", name_en: "San Stefano", name_ar: "سان ستيفانو", slug: "san-stefano",
        description: "A luxury hotel and residential area with seaside views. Perfect for romantic same-day deliveries.",
        keywords: kw("san stefano") },
      { name: "Sporting", name_en: "Sporting", name_ar: "سبورتنج", slug: "sporting",
        description: "A central seaside neighborhood between Cleopatra and Sidi Gaber. Strong local delivery network.",
        keywords: kw("sporting") },
      { name: "Montaza", name_en: "Montaza", name_ar: "المنتزه", slug: "montaza",
        description: "Eastern Alexandria district home to Montaza Palace gardens. Family-friendly delivery zone.",
        keywords: kw("montaza") },
      { name: "Sidi Bishr", name_en: "Sidi Bishr", name_ar: "سيدي بشر", slug: "sidi-bishr",
        description: "A popular eastern beach district. Same-day delivery for summer celebrations.",
        keywords: kw("sidi bishr") },
      { name: "Agami", name_en: "Agami", name_ar: "العجمي", slug: "agami",
        description: "A western beach area popular with summer visitors. Delivery available for vacation surprises.",
        keywords: kw("agami") },
      { name: "Borg El Arab", name_en: "Borg El Arab", name_ar: "برج العرب", slug: "borg-el-arab",
        description: "A western city home to industries and the international airport. Delivery within ~24 hours.",
        keywords: kw("borg el arab") },
      { name: "El Asafra", name_en: "El Asafra", name_ar: "العصافرة", slug: "el-asafra",
        description: "A bustling eastern Alexandria beach district between Mandara and Sidi Bishr, packed with summer apartment blocks and the Asafra metro station. Same-day delivery for vacation surprises.",
        keywords: kw("el asafra") },
      { name: "Mandara", name_en: "Mandara", name_ar: "المندرة", slug: "mandara",
        description: "An eastern coastal Alexandria neighborhood with beach access, busy markets, and large family apartment buildings. Trusted summer-season delivery.",
        keywords: kw("mandara") },
      { name: "El Mansheya", name_en: "El Mansheya", name_ar: "المنشية", slug: "el-mansheya",
        description: "Alexandria's historic central square district, anchored by the Mohamed Ali statue and the old commercial bazaars. Quick moped delivery to offices and apartments.",
        keywords: kw("el mansheya") },
      { name: "Anfoushi", name_en: "Anfoushi", name_ar: "الأنفوشي", slug: "anfoushi",
        description: "The historic seaside district at the western tip of Alexandria, home to the Anfoushi tombs and old fish market. Doorstep delivery to local apartment blocks.",
        keywords: kw("anfoushi") },
      { name: "El Raml", name_en: "El Raml", name_ar: "الرمل", slug: "el-raml",
        description: "The downtown Raml Station district where Alexandria's iconic blue tram begins, surrounded by belle-epoque buildings and Saad Zaghloul Square. Fast same-day delivery.",
        keywords: kw("el raml") },
      { name: "Ras El Tin", name_en: "Ras El Tin", name_ar: "رأس التين", slug: "ras-el-tin",
        description: "A historic western Alexandria district anchored by Ras El Tin Palace and the western harbor. Trusted apartment delivery in the old grid.",
        keywords: kw("ras el tin") },
      { name: "El Atareen", name_en: "El Atareen", name_ar: "العطارين", slug: "el-atareen",
        description: "Alexandria's traditional spice-and-antique quarter just south of Mansheya, with narrow lanes of old shops and apartment blocks. Quick on-foot delivery from our Mansheya couriers.",
        keywords: kw("el atareen") },
      { name: "Kafr Abdou", name_en: "Kafr Abdou", name_ar: "كفر عبده", slug: "kafr-abdou",
        description: "An upscale Roushdy pocket of leafy streets, embassies, and converted villas. Reliable same-day delivery for refined arrangements to apartments and offices.",
        keywords: kw("kafr abdou") },
      { name: "Loran", name_en: "Loran", name_ar: "لوران", slug: "loran",
        description: "A calm seaside neighborhood between Roushdy and Sidi Bishr, with the Loran tram station and quiet residential streets. Smooth doorstep delivery.",
        keywords: kw("loran") },
      { name: "Cleopatra", name_en: "Cleopatra", name_ar: "كليوباترا", slug: "cleopatra",
        description: "A central Alexandria neighborhood between Sporting and Sidi Gaber, with the Cleopatra tram station and the Greek Hospital. Same-day delivery for clinics and homes.",
        keywords: kw("cleopatra alexandria") },
      { name: "Camp Cesar", name_en: "Camp Cesar", name_ar: "كامب شيزار", slug: "camp-cesar",
        description: "A historic seaside district near Sporting, named for a Roman-era camp, with the Faculty of Fine Arts and quiet residential blocks. Trusted local delivery.",
        keywords: kw("camp cesar") },
      { name: "El Wardian", name_en: "El Wardian", name_ar: "الوردية", slug: "el-wardian",
        description: "A working-port district in western Alexandria near the harbor and cement works, with strong community markets. Reliable same-day delivery.",
        keywords: kw("el wardian") },
      { name: "Bahary", name_en: "Bahary", name_ar: "بحري", slug: "bahary",
        description: "Alexandria's northwestern seaside quarter, home to the famous Bahary fish market, Abu El Abbas mosque, and historic apartment blocks. Quick moped delivery from Mansheya.",
        keywords: kw("bahary") },
      { name: "Abu Qir", name_en: "Abu Qir", name_ar: "أبو قير", slug: "abu-qir",
        description: "An eastern coastal town beyond Montaza, famous for its fish restaurants and the site of the historic naval battle. Same-day delivery in summer season.",
        keywords: kw("abu qir") },
    ],
  },

  {
    name: "Damanhur",
    name_en: "Damanhur",
    name_ar: "دمنهور",
    slug: "damanhur",
    description: "Capital of Beheira governorate in the western Delta. An agricultural and academic hub with regional importance.",
    areas: [
      { name: "Damanhur City Center", name_en: "Damanhur City Center", name_ar: "وسط مدينة دمنهور", slug: "damanhur-center",
        description: "The downtown core with markets and the university. Delivery within Alexandria-Damanhur axis.",
        keywords: kw("damanhur") },
      { name: "Kafr El Dawar", name_en: "Kafr El Dawar", name_ar: "كفر الدوار", slug: "kafr-el-dawar",
        description: "An industrial city between Alexandria and Damanhur. Same-day delivery from Alex.",
        keywords: kw("kafr el dawar") },
      { name: "Rashid", name_en: "Rashid", name_ar: "رشيد", slug: "rashid",
        description: "The historic port-town of Rosetta at the Nile's western mouth, famous for the Rosetta Stone discovery site and Ottoman-era houses. Scheduled delivery from the Alexandria hub.",
        keywords: kw("rashid") },
      { name: "Edku", name_en: "Edku", name_ar: "إدكو", slug: "edku",
        description: "A coastal town between Alexandria and Rashid known for its fish market and the Edku gas processing plant. Same-day delivery from the Alex hub.",
        keywords: kw("edku") },
      { name: "Abu Hummus", name_en: "Abu Hummus", name_ar: "أبو حمص", slug: "abu-hummus",
        description: "A Delta agricultural town in Beheira on the Alexandria-Damanhur axis. Next-day delivery scheduled from Damanhur.",
        keywords: kw("abu hummus") },
    ],
  },

  {
    name: "Marsa Matruh",
    name_en: "Marsa Matruh",
    name_ar: "مرسى مطروح",
    slug: "marsa-matruh",
    description: "Capital of Matruh governorate on the Mediterranean coast. Summer destination with white-sand beaches.",
    areas: [
      { name: "Matruh City Center", name_en: "Matruh City Center", name_ar: "وسط مدينة مطروح", slug: "matruh-center",
        description: "The downtown along the corniche. Hotel and home delivery during the summer season.",
        keywords: kw("matruh") },
      { name: "Sidi Abdel Rahman", name_en: "Sidi Abdel Rahman", name_ar: "سيدي عبد الرحمن", slug: "sidi-abdel-rahman",
        description: "A premium resort area east of Marsa Matruh with luxury hotels and compounds. Vacation-week delivery.",
        keywords: kw("sidi abdel rahman") },
      { name: "El Alamein", name_en: "El Alamein", name_ar: "العلمين", slug: "el-alamein",
        description: "A historic coastal city at the WWII battlefield site, now home to the booming New Alamein City with its towers and university campus. Summer-season delivery to compounds and hotels.",
        keywords: kw("el alamein") },
      { name: "Marina", name_en: "Marina", name_ar: "مارينا", slug: "marina-matruh",
        description: "Egypt's flagship summer resort strip on the North Coast, with marinas, golf, and elite compounds. Premium vacation-cottage delivery during the summer season.",
        keywords: kw("marina north coast") },
      { name: "Salloum", name_en: "Salloum", name_ar: "السلوم", slug: "salloum",
        description: "Egypt's far northwestern coastal town on the Libyan border, with the historic Salloum plateau. Scheduled delivery from Marsa Matruh.",
        keywords: kw("salloum") },
    ],
  },

  // ========================================================================
  // CENTRAL DELTA (Gharbia, Menoufia, Kafr El Sheikh, Dakahlia, Damietta)
  // ========================================================================
  {
    name: "Tanta",
    name_en: "Tanta",
    name_ar: "طنطا",
    slug: "tanta",
    description: "The capital of Gharbia in the central Delta. Historic city with an annual mawlid festival and major university.",
    areas: [
      { name: "Tanta City Center", name_en: "Tanta City Center", name_ar: "وسط مدينة طنطا", slug: "tanta-center",
        description: "The downtown with markets, the mosque, and the medical district. Reliable same-day delivery.",
        keywords: kw("tanta") },
      { name: "El Mahalla El Kubra", name_en: "El Mahalla El Kubra", name_ar: "المحلة الكبرى", slug: "mahalla-el-kubra",
        description: "A major textile city in Gharbia, larger than Tanta in population. Same-day delivery from Tanta.",
        keywords: kw("mahalla el kubra") },
      { name: "Kafr El Zayat", name_en: "Kafr El Zayat", name_ar: "كفر الزيات", slug: "kafr-el-zayat",
        description: "An industrial city in Gharbia known for its cotton trade. Next-day delivery available.",
        keywords: kw("kafr el zayat") },
      { name: "Quesna", name_en: "Quesna", name_ar: "قويسنا", slug: "quesna",
        description: "An industrial town in Menoufia on the Cairo-Tanta agricultural road, with the Quesna industrial zone and pharmaceutical plants. Reliable next-day delivery from Tanta.",
        keywords: kw("quesna") },
      { name: "Berket El Sab", name_en: "Berket El Sab", name_ar: "بركة السبع", slug: "berket-el-sab",
        description: "A market town in Menoufia known for its agricultural produce and weekly Wednesday market. Next-day delivery from the Tanta hub.",
        keywords: kw("berket el sab") },
      { name: "Tala", name_en: "Tala", name_ar: "تلا", slug: "tala",
        description: "A historic Gharbia town with a major Coptic community and centuries-old churches. Next-day delivery from Tanta.",
        keywords: kw("tala") },
      { name: "Zifta", name_en: "Zifta", name_ar: "زفتى", slug: "zifta",
        description: "A Nile-branch town in Gharbia paired with Mit Ghamr across the river, with the famous Zifta bridge and busy market. Same-day delivery from Tanta and Mansoura.",
        keywords: kw("zifta") },
    ],
  },

  {
    name: "Shibin El Kom",
    name_en: "Shibin El Kom",
    name_ar: "شبين الكوم",
    slug: "shibin-el-kom",
    description: "Capital of Menoufia governorate. University town and agricultural hub in the southern Delta.",
    areas: [
      { name: "Shibin El Kom Center", name_en: "Shibin El Kom Center", name_ar: "وسط شبين الكوم", slug: "shibin-el-kom-center",
        description: "Downtown core with markets and the Menoufia University campus.",
        keywords: kw("shibin el kom") },
      { name: "Sadat City", name_en: "Sadat City", name_ar: "مدينة السادات", slug: "sadat-city",
        description: "A planned industrial city in Menoufia. Compound and factory deliveries available.",
        keywords: kw("sadat city") },
      { name: "Ashmoun", name_en: "Ashmoun", name_ar: "أشمون", slug: "ashmoun",
        description: "A historic Menoufia market town south of Shibin El Kom with strong agricultural ties. Next-day delivery from the Cairo and Tanta hubs.",
        keywords: kw("ashmoun") },
      { name: "Menouf", name_en: "Menouf", name_ar: "منوف", slug: "menouf",
        description: "An old Menoufia town named the governorate's namesake, with a teaching hospital and busy weekly markets. Next-day delivery scheduled from Tanta.",
        keywords: kw("menouf") },
    ],
  },

  {
    name: "Kafr El Sheikh",
    name_en: "Kafr El Sheikh",
    name_ar: "كفر الشيخ",
    slug: "kafr-el-sheikh",
    description: "Northern Delta governorate capital. Agricultural region close to Lake Burullus.",
    areas: [
      { name: "Kafr El Sheikh Center", name_en: "Kafr El Sheikh Center", name_ar: "وسط كفر الشيخ", slug: "kafr-el-sheikh-center",
        description: "Downtown with markets and the university. Next-day delivery from the Cairo hub.",
        keywords: kw("kafr el sheikh") },
      { name: "Desouk", name_en: "Desouk", name_ar: "دسوق", slug: "desouk",
        description: "A historic city in Kafr El Sheikh known for its mosque and Nile branch. Delivery available.",
        keywords: kw("desouk") },
      { name: "Fuwa", name_en: "Fuwa", name_ar: "فوة", slug: "fuwa",
        description: "A historic Mamluk-era town in Kafr El Sheikh known for its dozens of preserved mosques and Rosetta branch riverside. Scheduled next-day delivery.",
        keywords: kw("fuwa") },
      { name: "Baltim", name_en: "Baltim", name_ar: "بلطيم", slug: "baltim",
        description: "A Mediterranean coastal resort town in Kafr El Sheikh, very popular in summer with Delta families. Same-day delivery during the summer season.",
        keywords: kw("baltim") },
      { name: "Sidi Salem", name_en: "Sidi Salem", name_ar: "سيدي سالم", slug: "sidi-salem",
        description: "An agricultural town in central Kafr El Sheikh with the Sidi Salem weekly market. Next-day delivery from the Tanta and Alex hubs.",
        keywords: kw("sidi salem") },
    ],
  },

  {
    name: "Mansoura",
    name_en: "Mansoura",
    name_ar: "المنصورة",
    slug: "mansoura",
    description: "Capital of Dakahlia in the eastern Delta. Historic university city on a branch of the Nile.",
    areas: [
      { name: "Mansoura Center", name_en: "Mansoura Center", name_ar: "وسط المنصورة", slug: "mansoura-center",
        description: "Downtown with the corniche, universities, and medical district. Strong same-day delivery.",
        keywords: kw("mansoura") },
      { name: "Talkha", name_en: "Talkha", name_ar: "طلخا", slug: "talkha",
        description: "An industrial twin city across the Nile from Mansoura. Reliable cross-bridge delivery.",
        keywords: kw("talkha") },
      { name: "Mit Ghamr", name_en: "Mit Ghamr", name_ar: "ميت غمر", slug: "mit-ghamr",
        description: "A major city in southern Dakahlia. Delivery within a Mansoura-day cycle.",
        keywords: kw("mit ghamr") },
      { name: "New Mansoura", name_en: "New Mansoura", name_ar: "المنصورة الجديدة", slug: "new-mansoura",
        description: "A newly-developed coastal city north of Mansoura with towers, a corniche, and the New Mansoura University. Same-day delivery to compounds and beachfront apartments.",
        keywords: kw("new mansoura") },
      { name: "Aga", name_en: "Aga", name_ar: "أجا", slug: "aga",
        description: "A Dakahlia town east of Mansoura known for its agricultural fields and the famous Aga railway station. Reliable next-day delivery from Mansoura.",
        keywords: kw("aga") },
      { name: "Sherbeen", name_en: "Sherbeen", name_ar: "شربين", slug: "sherbeen",
        description: "A historic Delta town in northern Dakahlia on the Damietta branch of the Nile, with a busy weekly market. Same-day delivery from the Mansoura hub.",
        keywords: kw("sherbeen") },
      { name: "Beni Ebeid", name_en: "Beni Ebeid", name_ar: "بني عبيد", slug: "beni-ebeid",
        description: "A small Dakahlia market town between Mansoura and Mit Ghamr. Next-day delivery within the Mansoura cycle.",
        keywords: kw("beni ebeid") },
    ],
  },

  {
    name: "Damietta",
    name_en: "Damietta",
    name_ar: "دمياط",
    slug: "damietta",
    description: "Coastal city at the eastern Nile mouth. Known for furniture industry, the corniche, and Ras El Bar resort.",
    areas: [
      { name: "Damietta Center", name_en: "Damietta Center", name_ar: "وسط دمياط", slug: "damietta-center",
        description: "Downtown with the corniche, furniture market, and old quarters.",
        keywords: kw("damietta") },
      { name: "Ras El Bar", name_en: "Ras El Bar", name_ar: "رأس البر", slug: "ras-el-bar",
        description: "A summer beach resort at the Nile mouth. Vacation-season delivery.",
        keywords: kw("ras el bar") },
      { name: "New Damietta", name_en: "New Damietta", name_ar: "دمياط الجديدة", slug: "new-damietta",
        description: "A modern planned city north of old Damietta. Compound delivery available.",
        keywords: kw("new damietta") },
      { name: "Faraskour", name_en: "Faraskour", name_ar: "فارسكور", slug: "faraskour",
        description: "An agricultural Delta town in Damietta governorate with strong dairy and orchard industries. Next-day delivery from the Damietta hub.",
        keywords: kw("faraskour") },
      { name: "Zarqa", name_en: "Zarqa", name_ar: "الزرقا", slug: "zarqa-damietta",
        description: "A small Damietta town between Faraskour and Sherbeen with a busy local market. Scheduled delivery from the Damietta hub.",
        keywords: kw("zarqa damietta") },
    ],
  },

  // ========================================================================
  // CANAL ZONE (Port Said, Ismailia, Suez)
  // ========================================================================
  {
    name: "Port Said",
    name_en: "Port Said",
    name_ar: "بورسعيد",
    slug: "port-said",
    description: "Mediterranean port at the northern entrance of the Suez Canal. Historic free-zone city.",
    areas: [
      { name: "Port Said Center", name_en: "Port Said Center", name_ar: "وسط بورسعيد", slug: "port-said-center",
        description: "The downtown core with the lighthouse, museum, and free-zone shops.",
        keywords: kw("port said") },
      { name: "Al Manakh", name_en: "Al Manakh", name_ar: "المناخ", slug: "al-manakh",
        description: "A central Port Said district with markets and apartments.",
        keywords: kw("al manakh") },
      { name: "Port Fouad", name_en: "Port Fouad", name_ar: "بورفؤاد", slug: "port-fouad",
        description: "The smaller twin city across the Canal in Sinai. Ferry-served delivery zone.",
        keywords: kw("port fouad") },
      { name: "El Arab", name_en: "El Arab", name_ar: "العرب", slug: "port-said-el-arab",
        description: "A central Port Said residential district with the Arab district school and family apartment buildings. Trusted same-day delivery.",
        keywords: kw("port said el arab") },
      { name: "El Zohour", name_en: "El Zohour", name_ar: "الزهور", slug: "port-said-el-zohour",
        description: "A leafy planned residential district in southern Port Said with the Zohour gardens and family villas. Same-day delivery for celebrations.",
        keywords: kw("port said el zohour") },
    ],
  },

  {
    name: "Ismailia",
    name_en: "Ismailia",
    name_ar: "الإسماعيلية",
    slug: "ismailia",
    description: "Canal city on Lake Timsah, with leafy boulevards, the Suez Canal Authority, and a calm community feel.",
    areas: [
      { name: "Ismailia City Center", name_en: "Ismailia City Center", name_ar: "وسط الإسماعيلية", slug: "ismailia-center",
        description: "Downtown along Lake Timsah with cafes, markets, and the corniche.",
        keywords: kw("ismailia") },
      { name: "Fayed", name_en: "Fayed", name_ar: "فايد", slug: "fayed",
        description: "A lakeside resort town south of Ismailia. Vacation-cottage delivery available.",
        keywords: kw("fayed") },
      { name: "Tel El Kebir", name_en: "Tel El Kebir", name_ar: "تل الكبير", slug: "tel-el-kebir",
        description: "A historic town west of Ismailia. Next-day delivery from the Ismailia hub.",
        keywords: kw("tel el kebir") },
      { name: "Abu Sweir", name_en: "Abu Sweir", name_ar: "أبو صوير", slug: "abu-sweir",
        description: "A canal-side town in Ismailia governorate with the Abu Sweir air base. Same-day delivery from the Ismailia hub.",
        keywords: kw("abu sweir") },
      { name: "Qantara", name_en: "Qantara", name_ar: "القنطرة", slug: "qantara",
        description: "A historic Canal-crossing town north of Ismailia, split between East and West Qantara on either side of the Canal. Scheduled ferry-coordinated delivery.",
        keywords: kw("qantara") },
    ],
  },

  {
    name: "Suez",
    name_en: "Suez",
    name_ar: "السويس",
    slug: "suez",
    description: "Strategic Red Sea port at the southern mouth of the Canal, with industrial and port-side neighbourhoods.",
    areas: [
      { name: "Suez Center", name_en: "Suez Center", name_ar: "وسط السويس", slug: "suez-center",
        description: "Downtown with the port, markets, and main corniche.",
        keywords: kw("suez") },
      { name: "Ataqa", name_en: "Ataqa", name_ar: "عتاقة", slug: "ataqa",
        description: "An industrial district on the Suez Bay with refineries and the new logistics zone.",
        keywords: kw("ataqa") },
      { name: "Ain Sokhna", name_en: "Ain Sokhna", name_ar: "العين السخنة", slug: "ain-sokhna",
        description: "A coastal resort area east of Suez. Same-week delivery to compounds and hotels.",
        keywords: kw("ain sokhna") },
      { name: "Arbaeen", name_en: "Arbaeen", name_ar: "الأربعين", slug: "arbaeen",
        description: "A central Suez residential district with the famous Arbaeen square and busy local markets. Reliable same-day delivery.",
        keywords: kw("arbaeen") },
      { name: "Faisal", name_en: "Faisal", name_ar: "فيصل", slug: "suez-faisal",
        description: "A modern Suez residential district with apartment blocks and the Faisal main road. Quick same-day delivery for family celebrations.",
        keywords: kw("suez faisal") },
    ],
  },

  // ========================================================================
  // SINAI & RED SEA (Sharm El Sheikh, Arish, Hurghada)
  // ========================================================================
  {
    name: "Sharm El Sheikh",
    name_en: "Sharm El Sheikh",
    name_ar: "شرم الشيخ",
    slug: "sharm-el-sheikh",
    description: "A stunning Red Sea resort city known for its beautiful beaches, coral reefs, and luxury hotels. Perfect for romantic flower deliveries and vacation celebrations.",
    areas: [
      { name: "Naama Bay", name_en: "Naama Bay", name_ar: "خليج نعمة", slug: "naama-bay",
        description: "The main tourist area with beautiful beaches, restaurants, and nightlife. Ideal for flower deliveries to vacationers and romantic getaways.",
        keywords: kw("naama bay") },
      { name: "Old Market", name_en: "Old Market", name_ar: "السوق القديم", slug: "old-market",
        description: "Traditional market area with authentic Egyptian atmosphere. Perfect for cultural flower deliveries and local experiences.",
        keywords: kw("old market sharm") },
      { name: "Hadaba", name_en: "Hadaba", name_ar: "الهضبة", slug: "hadaba",
        description: "A central residential and hotel district overlooking the bay. Reliable in-room delivery.",
        keywords: kw("hadaba sharm") },
      { name: "Shark's Bay", name_en: "Shark's Bay", name_ar: "خليج القرش", slug: "sharks-bay",
        description: "A resort enclave with five-star hotels and dive centers near the airport.",
        keywords: kw("sharks bay") },
      { name: "Nabq", name_en: "Nabq", name_ar: "نبق", slug: "nabq",
        description: "A northern resort strip with all-inclusive hotels and the protected area.",
        keywords: kw("nabq") },
      { name: "Ras Nasrany", name_en: "Ras Nasrany", name_ar: "رأس نصراني", slug: "ras-nasrany",
        description: "A coastal stretch east of Naama Bay with premium hotels and the famous Tiran island dive sites offshore. Concierge in-room delivery.",
        keywords: kw("ras nasrany") },
      { name: "Soho Square", name_en: "Soho Square", name_ar: "سوهو سكوير", slug: "soho-square",
        description: "A modern open-air mall and entertainment plaza in Sharm with fountains, restaurants, and an ice-skating rink. Same-evening delivery for date nights.",
        keywords: kw("soho square") },
      { name: "Naama Heights", name_en: "Naama Heights", name_ar: "مرتفعات نعمة", slug: "naama-heights",
        description: "A hillside residential and hotel zone above Naama Bay with panoramic Red Sea views. Reliable hotel and villa delivery.",
        keywords: kw("naama heights") },
      { name: "Shark's Bay Marina", name_en: "Shark's Bay Marina", name_ar: "مارينا خليج القرش", slug: "sharks-bay-marina",
        description: "The yacht and dive marina at Shark's Bay near the airport, with five-star hotels and dive boats. Concierge delivery to yachts and suites.",
        keywords: kw("sharks bay marina") },
    ],
  },

  {
    name: "Dahab",
    name_en: "Dahab",
    name_ar: "دهب",
    slug: "dahab",
    description: "Laid-back diving and kitesurfing town on the Sinai coast. Small but well-served by our courier network.",
    areas: [
      { name: "Mashraba", name_en: "Mashraba", name_ar: "المشربة", slug: "mashraba",
        description: "Central Dahab's main beachfront strip with restaurants and dive shops.",
        keywords: kw("mashraba") },
      { name: "Assalah", name_en: "Assalah", name_ar: "الأصلة", slug: "assalah",
        description: "The original Bedouin quarter north of Mashraba.",
        keywords: kw("assalah") },
      { name: "Lighthouse", name_en: "Lighthouse", name_ar: "اللايت هاوس", slug: "dahab-lighthouse",
        description: "Dahab's famous dive-beach district, named for the iconic shore lighthouse and home to the favorite shore-dive entry points. Hotel and camp delivery.",
        keywords: kw("dahab lighthouse") },
      { name: "Laguna", name_en: "Laguna", name_ar: "اللاجونا", slug: "dahab-laguna",
        description: "The southern Dahab kitesurf district with shallow flatwater lagoons and beach hotels. In-room delivery to dive lodges and bungalows.",
        keywords: kw("dahab laguna") },
    ],
  },

  {
    name: "Arish",
    name_en: "Arish",
    name_ar: "العريش",
    slug: "arish",
    description: "Capital of North Sinai on the Mediterranean coast. Coastal city with Bedouin heritage.",
    areas: [
      { name: "Arish Center", name_en: "Arish Center", name_ar: "وسط العريش", slug: "arish-center",
        description: "Downtown with the corniche and market. Delivery subject to coordination.",
        keywords: kw("arish") },
      { name: "Sheikh Zuweid", name_en: "Sheikh Zuweid", name_ar: "الشيخ زويد", slug: "sheikh-zuweid",
        description: "A North Sinai town east of Arish with Bedouin heritage and the local university campus. Scheduled delivery from the Arish hub.",
        keywords: kw("sheikh zuweid") },
      { name: "Bir El Abd", name_en: "Bir El Abd", name_ar: "بئر العبد", slug: "bir-el-abd",
        description: "A coastal North Sinai town west of Arish, with the Bir El Abd salt lakes and small market. Scheduled delivery from the Arish hub.",
        keywords: kw("bir el abd") },
    ],
  },

  {
    name: "Hurghada",
    name_en: "Hurghada",
    name_ar: "الغردقة",
    slug: "hurghada",
    description: "Red Sea diving and resort capital, with Hurghada Marina, El Dahar, and Sahl Hasheesh resorts.",
    areas: [
      { name: "Hurghada Marina", name_en: "Hurghada Marina", name_ar: "مارينا الغردقة", slug: "hurghada-marina",
        description: "The yacht-marina district with restaurants and shopping. Hotel and yacht delivery.",
        keywords: kw("hurghada marina") },
      { name: "El Dahar", name_en: "El Dahar", name_ar: "الدهار", slug: "el-dahar",
        description: "The historic old quarter of Hurghada with the local market and downtown feel.",
        keywords: kw("el dahar") },
      { name: "Sahl Hasheesh", name_en: "Sahl Hasheesh", name_ar: "سهل حشيش", slug: "sahl-hasheesh",
        description: "A premium resort enclave south of Hurghada. Concierge delivery to luxury hotels.",
        keywords: kw("sahl hasheesh") },
      { name: "El Gouna", name_en: "El Gouna", name_ar: "الجونة", slug: "el-gouna",
        description: "An upscale resort town north of Hurghada with lagoons and golf. Compound delivery available.",
        keywords: kw("el gouna") },
      { name: "Magawish", name_en: "Magawish", name_ar: "مجاويش", slug: "magawish",
        description: "A southern Hurghada beach district with the Magawish Hotel resorts and quiet family compounds. Same-day hotel and apartment delivery.",
        keywords: kw("magawish") },
      { name: "Mubarak 6", name_en: "Mubarak 6", name_ar: "مبارك 6", slug: "mubarak-6",
        description: "A planned residential district inland from Hurghada with workforce housing and family apartments. Reliable same-day delivery to apartment blocks.",
        keywords: kw("mubarak 6") },
      { name: "El Hadaba", name_en: "El Hadaba", name_ar: "الهضبة", slug: "hadaba-hurghada",
        description: "A hillside Hurghada district overlooking the marina with hotel apartments and the Hadaba Hotel strip. Concierge and home delivery.",
        keywords: kw("hadaba hurghada") },
      { name: "Esh El Hawady", name_en: "Esh El Hawady", name_ar: "عش الحواوي", slug: "esh-el-hawady",
        description: "A central Hurghada residential district inland of the resort strip, with family apartments and the busy local market. Same-day moped delivery.",
        keywords: kw("esh el hawady") },
      { name: "Old Hurghada", name_en: "Old Hurghada", name_ar: "الغردقة القديمة", slug: "old-hurghada",
        description: "The historic core of Hurghada around El Dahar market with the old mosque and the original fishing port. Trusted same-day delivery in the old town.",
        keywords: kw("old hurghada") },
    ],
  },

  // ========================================================================
  // EASTERN DELTA (Sharqia)
  // ========================================================================
  {
    name: "Zagazig",
    name_en: "Zagazig",
    name_ar: "الزقازيق",
    slug: "zagazig",
    description: "Capital of Sharqia governorate in the eastern Delta. University town with strong agricultural ties.",
    areas: [
      { name: "Zagazig Center", name_en: "Zagazig Center", name_ar: "وسط مدينة الزقازيق", slug: "zagazig-center",
        description: "The main city area with universities, agricultural facilities, and commercial districts.",
        keywords: kw("zagazig") },
      { name: "Bilbeis", name_en: "Bilbeis", name_ar: "بلبيس", slug: "bilbeis",
        description: "A historic city in Sharqia with strong Cairo-axis connectivity.",
        keywords: kw("bilbeis") },
      { name: "10th of Ramadan City", name_en: "10th of Ramadan City", name_ar: "العاشر من رمضان", slug: "10th-of-ramadan",
        description: "A major planned industrial city in Sharqia. Compound and factory deliveries.",
        keywords: kw("10th of ramadan") },
      { name: "Abu Hammad", name_en: "Abu Hammad", name_ar: "أبو حماد", slug: "abu-hammad",
        description: "A market town in northern Sharqia on the Cairo-Ismailia road, with a busy livestock and grain market. Same-day delivery on the Cairo-Ismailia axis.",
        keywords: kw("abu hammad") },
      { name: "Faqous", name_en: "Faqous", name_ar: "فاقوس", slug: "faqous",
        description: "A historic Sharqia town with strong agricultural ties and a major weekly market. Next-day delivery from the Cairo hub.",
        keywords: kw("faqous") },
      { name: "Mashtoul El Souk", name_en: "Mashtoul El Souk", name_ar: "مشتول السوق", slug: "mashtoul-el-souk",
        description: "A bustling market town in central Sharqia south of Zagazig, named for its famous weekly souk. Reliable next-day delivery.",
        keywords: kw("mashtoul el souk") },
    ],
  },

  // ========================================================================
  // UPPER EGYPT (Beni Suef -> Aswan)
  // ========================================================================
  {
    name: "Faiyum",
    name_en: "Faiyum",
    name_ar: "الفيوم",
    slug: "faiyum",
    description: "Oasis city southwest of Cairo, around Lake Qarun, with ancient waterwheels and a pottery tradition.",
    areas: [
      { name: "Faiyum Center", name_en: "Faiyum Center", name_ar: "وسط الفيوم", slug: "faiyum-center",
        description: "Downtown with the iconic waterwheels (sawaqi) and local markets.",
        keywords: kw("faiyum") },
      { name: "Tamiya", name_en: "Tamiya", name_ar: "طامية", slug: "tamiya",
        description: "A farming town in northern Faiyum. Next-day delivery from the Cairo hub.",
        keywords: kw("tamiya") },
      { name: "Sinnouris", name_en: "Sinnouris", name_ar: "سنورس", slug: "sinnouris",
        description: "An agricultural town in central Faiyum. Trusted local delivery.",
        keywords: kw("sinnouris") },
      { name: "Ibshaway", name_en: "Ibshaway", name_ar: "إبشواي", slug: "ibshaway",
        description: "A Faiyum town near Lake Qarun, famous for its weekly market and farming community. Next-day delivery from the Cairo and Faiyum hubs.",
        keywords: kw("ibshaway") },
      { name: "Tunis Village", name_en: "Tunis Village", name_ar: "قرية تونس", slug: "tunis-village",
        description: "An artisan pottery village on the south shore of Lake Qarun, a magnet for weekend visitors and pottery workshops. Scheduled weekend delivery for lodge stays.",
        keywords: kw("tunis village") },
    ],
  },

  {
    name: "Beni Suef",
    name_en: "Beni Suef",
    name_ar: "بني سويف",
    slug: "beni-suef",
    description: "Upper Egypt city about 100 km south of Cairo. University town with Nile corniche and historic monasteries nearby.",
    areas: [
      { name: "Beni Suef Center", name_en: "Beni Suef Center", name_ar: "وسط بني سويف", slug: "beni-suef-center",
        description: "Downtown with the corniche, university, and market.",
        keywords: kw("beni suef") },
      { name: "El Wasta", name_en: "El Wasta", name_ar: "الواسطى", slug: "el-wasta",
        description: "A town on the Cairo-Beni Suef axis. Same-day delivery from Cairo.",
        keywords: kw("el wasta") },
      { name: "Beba", name_en: "Beba", name_ar: "ببا", slug: "beba",
        description: "An agricultural Beni Suef town with strong cotton and grain trades. Next-day delivery from the Beni Suef hub.",
        keywords: kw("beba") },
      { name: "New Beni Suef", name_en: "New Beni Suef", name_ar: "بني سويف الجديدة", slug: "new-beni-suef",
        description: "A planned residential extension east of the Nile with the new university campus and government district. Same-day delivery to the new compounds.",
        keywords: kw("new beni suef") },
    ],
  },

  {
    name: "Minya",
    name_en: "Minya",
    name_ar: "المنيا",
    slug: "minya",
    description: "Upper Egypt governorate capital, the 'Bride of Upper Egypt' on the western bank of the Nile. University and archaeological hub.",
    areas: [
      { name: "Minya Center", name_en: "Minya Center", name_ar: "وسط المنيا", slug: "minya-center",
        description: "Downtown with the corniche and university campus.",
        keywords: kw("minya") },
      { name: "Mallawi", name_en: "Mallawi", name_ar: "ملوي", slug: "mallawi",
        description: "A market town in southern Minya with historic significance.",
        keywords: kw("mallawi") },
      { name: "New Minya", name_en: "New Minya", name_ar: "المنيا الجديدة", slug: "new-minya",
        description: "A planned residential extension east of the Nile.",
        keywords: kw("new minya") },
      { name: "Beni Mazar", name_en: "Beni Mazar", name_ar: "بني مزار", slug: "beni-mazar",
        description: "A market town in northern Minya governorate with a busy weekly souk. Next-day delivery scheduled from the Minya hub.",
        keywords: kw("beni mazar") },
      { name: "Maghagha", name_en: "Maghagha", name_ar: "مغاغة", slug: "maghagha",
        description: "A historic Nile-side town in northern Minya with an old downtown grid and a riverside corniche. Next-day delivery from Minya and Beni Suef.",
        keywords: kw("maghagha") },
    ],
  },

  {
    name: "Asyut",
    name_en: "Asyut",
    name_ar: "أسيوط",
    slug: "asyut",
    description: "Largest city in Upper Egypt, on the west bank of the Nile, with a long academic and trading history.",
    areas: [
      { name: "Asyut Center", name_en: "Asyut Center", name_ar: "وسط أسيوط", slug: "asyut-center",
        description: "Downtown with the university, market, and Nile corniche.",
        keywords: kw("asyut") },
      { name: "Manqabad", name_en: "Manqabad", name_ar: "منقباد", slug: "manqabad",
        description: "A western Asyut district with universities and residential areas.",
        keywords: kw("manqabad") },
      { name: "Dairut", name_en: "Dairut", name_ar: "ديروط", slug: "dairut",
        description: "A market town in northern Asyut governorate.",
        keywords: kw("dairut") },
      { name: "Abnoub", name_en: "Abnoub", name_ar: "أبنوب", slug: "abnoub",
        description: "An east-bank Asyut town across the Nile from Asyut city, with strong agricultural and Coptic community ties. Next-day delivery from the Asyut hub.",
        keywords: kw("abnoub") },
      { name: "Abu Tig", name_en: "Abu Tig", name_ar: "أبو تيج", slug: "abu-tig",
        description: "A south-Asyut market town along the Nile-axis road with a long Coptic monastic tradition. Reliable next-day delivery from Asyut.",
        keywords: kw("abu tig") },
    ],
  },

  {
    name: "Sohag",
    name_en: "Sohag",
    name_ar: "سوهاج",
    slug: "sohag",
    description: "Upper Egypt governorate capital on the west bank of the Nile, known for its university and the White and Red Monasteries.",
    areas: [
      { name: "Sohag Center", name_en: "Sohag Center", name_ar: "وسط سوهاج", slug: "sohag-center",
        description: "Downtown with the university, hospitals, and main markets.",
        keywords: kw("sohag") },
      { name: "Akhmim", name_en: "Akhmim", name_ar: "أخميم", slug: "akhmim",
        description: "A historic city across the Nile from Sohag, famous for handwoven textiles.",
        keywords: kw("akhmim") },
      { name: "Girga", name_en: "Girga", name_ar: "جرجا", slug: "girga",
        description: "A market town in southern Sohag.",
        keywords: kw("girga") },
      { name: "Tahta", name_en: "Tahta", name_ar: "طهطا", slug: "tahta",
        description: "A historic west-bank Sohag market town with a deep Coptic and Sufi heritage. Next-day delivery from the Sohag hub.",
        keywords: kw("tahta") },
      { name: "El Balyana", name_en: "El Balyana", name_ar: "البلينا", slug: "el-balyana",
        description: "A southern Sohag town and main access point to the Abydos temple complex. Scheduled delivery from Sohag.",
        keywords: kw("el balyana") },
    ],
  },

  {
    name: "Qena",
    name_en: "Qena",
    name_ar: "قنا",
    slug: "qena",
    description: "Upper Egypt governorate at the Nile bend, gateway to the Dendera and Abydos temples.",
    areas: [
      { name: "Qena Center", name_en: "Qena Center", name_ar: "وسط قنا", slug: "qena-center",
        description: "Downtown with the university and the Dendera Temple nearby.",
        keywords: kw("qena") },
      { name: "Nag Hammadi", name_en: "Nag Hammadi", name_ar: "نجع حمادي", slug: "nag-hammadi",
        description: "An industrial town in northern Qena.",
        keywords: kw("nag hammadi") },
      { name: "Qus", name_en: "Qus", name_ar: "قوص", slug: "qus",
        description: "A historic city south of Qena.",
        keywords: kw("qus") },
      { name: "Dishna", name_en: "Dishna", name_ar: "دشنا", slug: "dishna",
        description: "An agricultural Qena town along the Nile north of the city, famous for the Dishna Papyri find. Next-day delivery from the Qena hub.",
        keywords: kw("dishna") },
      { name: "Farshout", name_en: "Farshout", name_ar: "فرشوط", slug: "farshout",
        description: "A western Qena town with a strong sugarcane farming economy and the local Coptic monastery. Scheduled delivery from Qena.",
        keywords: kw("farshout") },
    ],
  },

  {
    name: "Luxor",
    name_en: "Luxor",
    name_ar: "الأقصر",
    slug: "luxor",
    description: "The world's greatest open-air museum, home to ancient temples and tombs. A magical destination for unique flower deliveries and historical celebrations.",
    areas: [
      { name: "East Bank", name_en: "East Bank", name_ar: "البر الشرقي", slug: "east-bank",
        description: "Home to Luxor Temple and Karnak Temple, the heart of ancient Thebes. Perfect for flower deliveries to tourists and cultural celebrations.",
        keywords: kw("east bank luxor") },
      { name: "West Bank", name_en: "West Bank", name_ar: "البر الغربي", slug: "west-bank",
        description: "The Valley of the Kings, Hatshepsut Temple, and the Colossi of Memnon. Hotel and cruise delivery.",
        keywords: kw("west bank luxor") },
      { name: "Karnak", name_en: "Karnak", name_ar: "الكرنك", slug: "karnak",
        description: "The northern district housing the great Karnak Temple complex and nearby residential streets.",
        keywords: kw("karnak") },
      { name: "Esna", name_en: "Esna", name_ar: "إسنا", slug: "esna",
        description: "A southern Luxor governorate town famous for the Khnum Temple and its old caravanserai market. Same-day delivery from the Luxor hub.",
        keywords: kw("esna") },
      { name: "Armant", name_en: "Armant", name_ar: "أرمنت", slug: "armant",
        description: "A historic west-bank town south of Luxor, named for the ancient Hermonthis and known for its sugarcane mills. Same-day delivery from Luxor.",
        keywords: kw("armant") },
      { name: "El Tod", name_en: "El Tod", name_ar: "الطود", slug: "el-tod",
        description: "An east-bank town south of Luxor with the ancient temple of Montu and busy farming fields along the Nile. Same-day delivery from the Luxor hub.",
        keywords: kw("el tod") },
      { name: "New Tiba", name_en: "New Tiba", name_ar: "طيبة الجديدة", slug: "new-tiba",
        description: "A newly-developed residential extension west of Luxor, named after ancient Thebes, with modern housing and government offices. Same-day delivery from the Luxor hub.",
        keywords: kw("new tiba") },
    ],
  },

  {
    name: "Aswan",
    name_en: "Aswan",
    name_ar: "أسوان",
    slug: "aswan",
    description: "The beautiful southern city of Egypt, known for its Nubian culture, ancient temples, and stunning Nile views. A perfect destination for unique flower deliveries and cultural celebrations.",
    areas: [
      { name: "Aswan City Center", name_en: "Aswan City Center", name_ar: "وسط مدينة أسوان", slug: "aswan-city-center",
        description: "The heart of Aswan with its vibrant markets, hotels, and cultural sites.",
        keywords: kw("aswan city center") },
      { name: "Elephantine Island", name_en: "Elephantine Island", name_ar: "جزيرة الفنتين", slug: "elephantine-island",
        description: "A historic island in the Nile with ancient temples and Nubian villages.",
        keywords: kw("elephantine island") },
      { name: "Kitchener Island", name_en: "Kitchener Island", name_ar: "جزيرة كيتشنر", slug: "kitchener-island",
        description: "A beautiful botanical garden island known for its exotic plants and peaceful atmosphere.",
        keywords: kw("kitchener island") },
      { name: "Aswan High Dam", name_en: "Aswan High Dam", name_ar: "السد العالي", slug: "high-dam",
        description: "The High Dam area south of the city, with views of Lake Nasser.",
        keywords: kw("aswan high dam") },
      { name: "Kom Ombo", name_en: "Kom Ombo", name_ar: "كوم أمبو", slug: "kom-ombo",
        description: "A northern Aswan governorate town famous for the dual Kom Ombo temple and the sugarcane plantations on the Nile bank. Same-day delivery from the Aswan hub.",
        keywords: kw("kom ombo") },
      { name: "Edfu", name_en: "Edfu", name_ar: "إدفو", slug: "edfu",
        description: "A west-bank town between Luxor and Aswan, home to the perfectly preserved Temple of Horus. Same-day delivery from the Aswan and Luxor hubs.",
        keywords: kw("edfu") },
      { name: "Daraw", name_en: "Daraw", name_ar: "دراو", slug: "daraw",
        description: "A southern Aswan governorate town famous for its weekly camel market and Sudanese trade routes. Reliable same-day delivery from the Aswan hub.",
        keywords: kw("daraw") },
      { name: "Kalabsha", name_en: "Kalabsha", name_ar: "كلابشة", slug: "kalabsha",
        description: "A historic Nubian area near Lake Nasser, home to the relocated Kalabsha temple complex. Scheduled delivery from the Aswan hub.",
        keywords: kw("kalabsha") },
    ],
  },

  // ========================================================================
  // WESTERN DESERT (New Valley)
  // ========================================================================
  {
    name: "Kharga",
    name_en: "Kharga",
    name_ar: "الخارجة",
    slug: "kharga",
    description: "Capital of the New Valley governorate, the largest oasis in the Western Desert. Delivery is scheduled in advance.",
    areas: [
      { name: "Kharga Center", name_en: "Kharga Center", name_ar: "وسط الخارجة", slug: "kharga-center",
        description: "The main town of the oasis with markets and government offices.",
        keywords: kw("kharga") },
      { name: "Dakhla", name_en: "Dakhla", name_ar: "الداخلة", slug: "dakhla",
        description: "A second major oasis in the New Valley, west of Kharga.",
        keywords: kw("dakhla") },
      { name: "Farafra", name_en: "Farafra", name_ar: "الفرافرة", slug: "farafra",
        description: "A remote oasis in the New Valley, gateway to the White Desert national park. Scheduled delivery to lodges and homes.",
        keywords: kw("farafra") },
      { name: "Baris", name_en: "Baris", name_ar: "باريس", slug: "baris",
        description: "A small oasis settlement south of Kharga with a historic mud-brick old town and date palm groves. Scheduled delivery from the Kharga hub.",
        keywords: kw("baris") },
    ],
  },
];

export const getAllCities = () => egyptianLocations;

export const getCityBySlug = (slug: string) =>
  egyptianLocations.find(city => city.slug === slug);

export const getAreaBySlug = (citySlug: string, areaSlug: string) => {
  const city = getCityBySlug(citySlug);
  return city?.areas.find(area => area.slug === areaSlug);
};
